<template>
  <div>
    <v-card flat>
      <v-card-title class="text-h5 font-weight-bold">
        {{ $t('General Settings') }}
        <v-btn
          v-if="subdomain"
          class="ml-2"
          small
          text
          color="primary"
          :href="`https://${subdomain}.bookingsboom.com`"
          target="_blank"
          rel="noopener"
        >
          <v-icon small class="mr-1">mdi-open-in-new</v-icon>
          {{ `${subdomain}.bookingsboom.com` }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form ref="form">
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-4 mb-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Site Settings') }}
                </v-card-title>

                <v-text-field
                  v-model.trim="formData.name"
                  dense
                  outlined
                  :rules="[required, nameRule]"
                  hint="* Only lowercase letters, numbers, and hyphens allowed"
                  :persistent-hint="true"
                  :label="`${$t('Name')} (subdomain)`"
                  @input="updateForm"
                />

                <v-text-field
                  v-model.trim="formData.site_title"
                  dense
                  outlined
                  :rules="[required]"
                  :label="$t('Website Title')"
                  @input="updateForm"
                />

                <v-text-field
                  v-model.trim="formData.custom_domain"
                  dense
                  outlined
                  hide-details
                  :label="$t('Custom Domain (Optional)')"
                  @input="updateForm"
                />

                <div class="pl-1 text-body-2">
                  {{
                    `Your domain: ${
                      formData.custom_domain || `${subdomain}.bookingsboom.com`
                    }`
                  }}
                </div>
                <div class="pl-1 text-caption grey--text">
                  * Domain modifications may take several hours to take effect.
                </div>

                <v-select
                  v-model="formData.site_type"
                  dense
                  outlined
                  :items="siteTypes"
                  :label="$t('Site Type')"
                  @change="updateForm"
                />

                <v-text-field
                  v-model.trim="formData.home_url"
                  dense
                  outlined
                  :label="$t('Custom Home URL')"
                  :hint="$t('Leave empty to use the default landing page')"
                  persistent-hint
                  placeholder="https://example.com"
                  @input="updateForm"
                />
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined class="pa-4 mb-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Theme Colors') }}
                </v-card-title>

                <v-row>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="formData.theme.actions_color"
                      hide-details
                      class="ma-0 pa-0"
                      :label="$t('Actions color')"
                      outlined
                      dense
                    >
                      <template #append>
                        <v-menu
                          v-model="menuActions"
                          :close-on-content-click="false"
                          offset-y
                        >
                          <template #activator="{ on }">
                            <div
                              :style="swatchStyle(formData.theme.actions_color)"
                              v-on="on"
                            />
                          </template>
                          <v-color-picker
                            v-model="formData.theme.actions_color"
                            mode="hexa"
                            @input="updateForm"
                          />
                        </v-menu>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="formData.theme.main_color"
                      hide-details
                      class="ma-0 pa-0"
                      :label="$t('Main color')"
                      outlined
                      dense
                    >
                      <template #append>
                        <v-menu
                          v-model="menuMain"
                          :close-on-content-click="false"
                          offset-y
                        >
                          <template #activator="{ on }">
                            <div
                              :style="swatchStyle(formData.theme.main_color)"
                              v-on="on"
                            />
                          </template>
                          <v-color-picker
                            v-model="formData.theme.main_color"
                            mode="hexa"
                            @input="updateForm"
                          />
                        </v-menu>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="formData.theme.secondary_color"
                      hide-details
                      class="ma-0 pa-0"
                      :label="$t('Secondary color')"
                      outlined
                      dense
                    >
                      <template #append>
                        <v-menu
                          v-model="menuSecondary"
                          :close-on-content-click="false"
                          offset-y
                        >
                          <template #activator="{ on }">
                            <div
                              :style="
                                swatchStyle(formData.theme.secondary_color)
                              "
                              v-on="on"
                            />
                          </template>
                          <v-color-picker
                            v-model="formData.theme.secondary_color"
                            mode="hexa"
                            @input="updateForm"
                          />
                        </v-menu>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-card>

              <v-card outlined class="pa-4">
                <v-card-title class="text-subtitle-1 px-0 pt-0">
                  {{ $t('Analytics') }}
                </v-card-title>

                <v-text-field
                  v-model="formData.analytics.google"
                  dense
                  outlined
                  :label="$t('Google Analytics (gtag)')"
                  :hint="$t('Add your Google tag ID from Google Analytics')"
                  persistent-hint
                  @input="updateForm"
                />

                <v-text-field
                  v-model="formData.analytics.gtm"
                  dense
                  outlined
                  :label="$t('Google Tag Manager (gtm)')"
                  @input="updateForm"
                />

                <v-text-field
                  v-model="formData.analytics.pixel"
                  dense
                  outlined
                  :label="$t('Facebook Pixel (Pixel Id)')"
                  @input="updateForm"
                />

                <v-checkbox
                  v-model="formData.show_cookie_consent"
                  :label="$t('Show Cookie Consent Banner')"
                  :hint="$t('Display a cookie consent banner to visitors')"
                  persistent-hint
                  @change="updateForm"
                />
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import FormRulesMixin from 'components/mixins/form-rules-mixin'

export default {
  name: 'GeneralSettings',

  mixins: [FormRulesMixin],

  props: {
    bookingEngineSetting: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      menuMain: false,
      menuSecondary: false,
      menuActions: false,
      formData: {
        name: this.bookingEngineSetting.name || '',
        site_title: this.bookingEngineSetting.site_title || '',
        custom_domain: this.bookingEngineSetting.custom_domain || '',
        site_type: this.bookingEngineSetting.site_type || 'vacation_rental',
        home_url: this.bookingEngineSetting.custom_settings?.home_url || '',
        analytics: {
          google: this.bookingEngineSetting.analytics?.google || '',
          gtm: this.bookingEngineSetting.analytics?.gtm || '',
          pixel: this.bookingEngineSetting.analytics?.pixel || '',
        },
        show_cookie_consent:
          this.bookingEngineSetting.show_cookie_consent || false,
        theme: {
          main_color: this.bookingEngineSetting.theme?.main_color || '#44a2a2',
          secondary_color:
            this.bookingEngineSetting.theme?.secondary_color || '#dfe0f2',
          actions_color:
            this.bookingEngineSetting.theme?.actions_color || '#557fc9',
        },
      },
      siteTypes: [
        { text: this.$t('Vacation Rental'), value: 'vacation_rental' },
        { text: this.$t('Hotel'), value: 'hotel' },
        { text: this.$t('Agent Reserve'), value: 'agent_reserve' },
      ],
    }
  },

  computed: {
    subdomain() {
      return this.formData.name
        ? this.formData.name
            .replace(/ /g, '')
            .replace(/\./g, '')
            .replace(/_/g, '')
            .toLowerCase()
        : ''
    },
  },

  watch: {
    bookingEngineSetting: {
      handler(newVal) {
        this.formData = {
          name: newVal.name || '',
          site_title: newVal.site_title || '',
          custom_domain: newVal.custom_domain || '',
          site_type: newVal.site_type || 'vacation_rental',
          home_url: newVal.custom_settings?.home_url || '',
          analytics: {
            google: newVal.analytics?.google || '',
            gtm: newVal.analytics?.gtm || '',
            pixel: newVal.analytics?.pixel || '',
          },
          show_cookie_consent: newVal.show_cookie_consent || false,
          theme: {
            main_color: newVal.theme?.main_color || '#44a2a2',
            secondary_color: newVal.theme?.secondary_color || '#dfe0f2',
            actions_color: newVal.theme?.actions_color || '#557fc9',
          },
        }
      },
      deep: true,
    },
  },

  methods: {
    swatchStyle(color) {
      return {
        backgroundColor: color,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: '4px',
      }
    },

    nameRule(v) {
      if (!v) return true
      const pattern = /^[a-z0-9-]+$/
      return (
        pattern.test(v) ||
        'Only lowercase letters, numbers, and hyphens allowed'
      )
    },

    updateForm() {
      if (this.$refs.form.validate()) {
        const { home_url, ...restFormData } = this.formData
        const updatedData = {
          ...this.bookingEngineSetting,
          ...restFormData,
          custom_settings: {
            ...this.bookingEngineSetting.custom_settings,
            home_url: home_url || '',
          },
          theme: {
            main_color: this.formData.theme.main_color,
            secondary_color: this.formData.theme.secondary_color,
            actions_color: this.formData.theme.actions_color,
          },
        }
        this.$emit('update:setting', updatedData)
      }
    },

    validate() {
      return this.$refs.form.validate()
    },
  },
}
</script>
